/**
 * ReportActionBar — "Bar 1" from the CB-Reporting Figma (Filter Bar/Topbar,
 * master 75:3671). Spec 092.
 *
 * The breadcrumb trail on the left, the page-action cluster on the right, one
 * row, aligned to the content column.
 *
 * WHAT BELONGS HERE, and why it is a rule rather than a preference — the
 * designer's own note on this cluster:
 *
 *   "Favourite, Ask and Export sit up here because they act on the whole report,
 *    not on the data inside it. None of them change what is in the table, so they
 *    stay out of the filter row."   — Himaad Riaz
 *
 * So the test for anything added later is: does it act on the report as an
 * object? If it changes which rows come back it belongs in the query card; if it
 * changes how the result is drawn it belongs in the grid header.
 *
 * Rendered by BOTH report hosts — ReportView (119 generic reports) and
 * SpecializedReportShell (21 specialized) — which is the whole of the coverage
 * story: those are the only two places report chrome is drawn, so this component
 * reaching both means it reaches 140/140.
 *
 * Layout's global <Breadcrumbs> band now owns the trail on /reports/:id — this
 * component publishes the report NAME to it and renders only the action cluster.
 * (It used to draw the trail here, on the same row. Jordan asked for the Queue
 * page's full-width strip everywhere, so there is one trail in one place now.)
 *
 * NOT here yet, deliberately:
 *   - Favorite. No starring data model exists; a write-only star is worse than no
 *     star. Its own ticket.
 *
 * DISCHARGED — this comment used to say the Ask trigger was absent on the
 * specialized path, pending "Piece 8". It has since landed on BOTH hosts:
 * `SpecializedReportShell.jsx:262` and `ReportView.jsx:997` each pass `ask`. The
 * note is kept rather than deleted because "the specialized pages have no Ask
 * trigger" is exactly the kind of statement that gets re-actioned as a gap a
 * second time — see the spec-049 phantom-gap entry in CLAUDE.md.
 */
import { Link, useLocation } from 'react-router-dom'
import { usePublishBreadcrumbLabel } from '@components/layout/breadcrumbLabelStore'
import { isEmbed } from '@/embed/embedConfig'
import ReportFeedback from './ReportFeedback'

const ReportActionBar = ({
  reportName,
  /* The report's one-line description, shown under the heading. */
  reportSummary,
  /* Back-to-catalog handler. Both hosts pass their `useBackNav` goBack. */
  onBack,
  reportsHref = '/reports',
  /* The Ask trigger, when the host has an AI panel to toggle. */
  ask,
  /* The export control. Passed in rather than constructed here: the two hosts
     hand ReportExport genuinely different props (a specialized page supplies its
     own server `download`; a generic one supplies a reportId), and inventing a
     union of those here would put path-specific knowledge in shared chrome. */
  children,
}) => {
  /*
   * Publish the report's name for the GLOBAL breadcrumb band rather than drawing a
   * trail here.
   *
   * This component used to render its own `<nav aria-label="Breadcrumb">` on the
   * same row as the actions. That gave the app two breadcrumb implementations with
   * the same accessible name and different looks — the Queue page got a
   * full-width white strip under the header, a report page got a borderless line
   * sharing a row with its buttons. Jordan asked for the Queue treatment
   * everywhere, so the band owns the trail and this row owns the actions.
   *
   * The name is published, not passed down, because the band is a SIBLING of
   * `<main>` in `Layout` — it renders above the routed page and cannot receive a
   * prop from it. Doing it here rather than in each host means both chrome hosts
   * (ReportView and SpecializedReportShell), and therefore all 140 reports, get it
   * from one edit; the embed gets it too, since it renders ReportView.
   *
   * THE EMBED IS THE ONE EXCEPTION, and it keeps its in-page trail.
   * `EmbedApp` has its own router and deliberately never mounts `Layout`
   * (`EmbedReportView.jsx:6-9`), so there is no global band there to hand the job
   * to. Dropping the trail unconditionally would therefore have deleted the
   * embed's only breadcrumb — a silent regression, and one an existing test
   * already covers with `reportsHref="/embed/reports"`. That is what `reportsHref`
   * is still for.
   *
   * Two render paths for one trail is normally the thing to avoid, and it is worth
   * saying why this is not that: the condition is not a page opting in, it is
   * whether a global band exists to defer to. The main app has exactly one trail;
   * the embed, which has no chrome at all, draws its own.
   */
  const { pathname } = useLocation()
  usePublishBreadcrumbLabel(pathname, reportName)
  const inEmbed = isEmbed()

  /*
   * The page HEADING lives on this row, so the actions sit beside it.
   *
   * They used to be a row of their own, above the heading — which after the
   * breadcrumb moved into the global band left three stacked rows of chrome
   * (strip, then an actions-only row, then the title) and a lot of dead space
   * before any content. Jordan: *"there is a lot of dead space now at the top of
   * reports — can we lower the 2 buttons on the right and then reclaim some lines
   * of empty space?"* One row does both: the buttons drop to the heading's line
   * and a whole row plus two margins disappear.
   *
   * The heading block moved in here rather than staying in the hosts because it
   * was **byte-identical** in both of them (back arrow + h1 + summary). One copy,
   * rendered for all 140 reports.
   *
   * `items-start` so the buttons align to the top of a heading that wraps to two
   * lines rather than floating to its vertical centre.
   */
  /*
   * Beta feedback (spec 123) — rendered HERE rather than passed in by each host.
   *
   * This bar is the one piece of chrome both `ReportView` (119 generic) and
   * `SpecializedReportShell` (21 specialized) render, so building the trigger in
   * reaches all 140 by construction. A `feedback` prop would have to be passed
   * by both call sites, and a page-opt-in prop is how this repo has repeatedly
   * shipped a feature that silently exists on only some paths.
   *
   * Not offered inside the IQ embed: that frame belongs to another product, and
   * a Report Center feedback box appearing in it would be filed against the
   * wrong team.
   *
   * The report context is read from the ROUTE rather than taken as a prop, for
   * the same no-opt-in reason. `/reports/190` → the generic id; `/reports/ar` →
   * a specialized key. If the segment is unrecognisable the feedback still
   * sends, just without a report attached.
   */
  const lastSegment = (pathname || '').split('?')[0].split('/').filter(Boolean).pop() || ''
  const numericId = /^\d+$/.test(lastSegment) ? lastSegment : null
  const feedback = inEmbed ? null : (
    <ReportFeedback
      reportId={numericId}
      reportKind={numericId ? 'generic' : (lastSegment || null)}
      reportName={reportName}
    />
  )

  /*
   * `feedback` is deliberately NOT part of `hasActions`. An otherwise-empty bar
   * still costs its bottom margin and leaves a floating gap above the grid — the
   * defect this row's own test pins. So feedback rides along with a bar that was
   * going to render anyway; it never forces one into existence. Every one of the
   * 140 reports has a `reportName`, so all 140 still get the button.
   */
  const hasActions = Boolean(ask || children)
  if (!inEmbed && !hasActions && !reportName) return null

  return (
    <>
      {inEmbed && (
        <nav aria-label="Breadcrumb" className="mb-3 flex min-w-0 items-center gap-1.5 text-sm">
          <Link to="/" className="text-rc-muted transition-colors hover:text-rc-ink">Home</Link>
          <span aria-hidden="true" className="text-rc-line">/</span>
          <Link to={reportsHref} className="text-rc-muted transition-colors hover:text-rc-ink">Reports</Link>
          {reportName && (
            <>
              <span aria-hidden="true" className="text-rc-line">/</span>
              {/* Long report names are common ("Shareholder Compensation for
                  Production and Originations"), so the trail truncates rather than
                  pushing anything off the row. */}
              <span aria-current="page" className="truncate font-medium text-rc-ink">{reportName}</span>
            </>
          )}
        </nav>
      )}

      <div data-print-center className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        {reportName && (
          <div className="flex min-w-0 items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                aria-label="Back to reports"
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
            )}
            <div className="min-w-0">
              <h1 className="text-2xl font-display text-gray-900">{reportName}</h1>
              {reportSummary && (
                <p className="text-sm text-gray-500 mt-0.5">{reportSummary}</p>
              )}
            </div>
          </div>
        )}

        {(hasActions || feedback) && (
          <div className="flex flex-none items-center gap-2">
            {ask}
            {children}
            {feedback}
          </div>
        )}
      </div>
    </>
  )
}

export default ReportActionBar
